import { useEffect, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import PublicAuthActions from "./PublicAuthActions";
import BackButton from "./BackButton";

/**
 * The chrome shared by the public pages built from the Claude Design files — header, gradient hero
 * band, content column and dark footer.
 *
 * <p>A SECOND shell, deliberately, alongside {@link PublicPageShell}. The design files are a
 * different visual language: Source Serif 4 over Figtree on warm #faf9f5, deep green #14532d with a
 * mint #86efac accent, a full-bleed gradient band and cards that lift into it. The older public
 * pages (pricing, trust, privacy, terms, contact) are still on #2C7A5B with Archivo/Public Sans, and
 * the two cannot share a shell without one of them looking wrong. When those pages are redesigned
 * this becomes the only one.</p>
 *
 * <p>The cards that follow the band overlap it by 28px — that is the design's own
 * <code>margin:-28px auto 0</code> on the content column, and it is why this owns the wrapper rather
 * than leaving each page to remember it.</p>
 */

/** The design's palette, named so the values are not scattered through the markup. */
export const C = {
  ground: "#faf9f5",
  ink: "#1c211c",
  inkMuted: "#5c6658",
  bodyMuted: "#6f7568",
  deep: "#14532d",
  link: "#16a34a",
  mint: "#86efac",
  mintInk: "#cde8d4",
  mintFaint: "#a7d4b4",
  tint: "#f0f7f1",
  line: "#eeece5",
  cardLine: "#e7e5de",
  bandLine: "#e2eee5",
  footInk: "#9a9f93",
} as const;

export const SERIF = "'Source Serif 4',serif";

/** The design's rounded action, used for every call to action on these pages. */
export const pill: CSSProperties = {
  borderRadius: 99,
  fontSize: 14,
  fontWeight: 700,
  padding: "12px 26px",
  textDecoration: "none",
  display: "inline-block",
};

/** The white card these pages are built from. */
export const card: CSSProperties = {
  background: "#fff",
  border: `1px solid ${C.cardLine}`,
  borderRadius: 20,
};

/** The rounded tinted tile a section icon sits in. */
export function IconTile({ glyph, size = 38 }: { glyph: string; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: size >= 38 ? 12 : 10,
        background: C.tint,
        display: "grid",
        placeItems: "center",
        fontSize: size >= 38 ? 17 : 15,
        flex: "none",
      }}
    >
      {glyph}
    </span>
  );
}

/** A card heading in the design's serif. */
export function CardTitle({ children, size = 21 }: { children: ReactNode; size?: number }) {
  return (
    <h2 style={{ fontFamily: SERIF, fontSize: size, fontWeight: 500, color: C.ink, margin: 0 }}>
      {children}
    </h2>
  );
}

/** Matches the design's <helmet>. */
const FONTS = [
  "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=Figtree:wght@400;500;600;700&display=swap",
];

const SHELL_CSS = `
.dcx-root a{color:${C.link};text-decoration:none}
.dcx-root a:hover{color:${C.deep}}
.dcx-pill{transition:opacity .15s}
.dcx-pill:hover{opacity:.88}
.dcx-split{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(260px,1fr);gap:24px;align-items:start}
/* The designs draw a fixed two-column split with no media query. Below this width the narrow
   column stops being readable, so it stacks — a rule the print-like mockups do not have to make
   but a real page does. */
@media (max-width:880px){.dcx-split{grid-template-columns:1fr}}
`;

export default function MarketplaceShell({
  metaTitle,
  metaDescription,
  eyebrow,
  title,
  intro,
  heroImage,
  showBack = false,
  header,
  children,
}: {
  metaTitle: string;
  metaDescription: string;
  /** The small tracked label above the headline — "ABOUT", "HOW IT WORKS". */
  eyebrow: string;
  /**
   * The serif headline. A node, because the designs bold a phrase inside it.
   *
   * <p>The bolded phrase picks its own colour, and it has to change with {@link heroImage}: the
   * designs use {@link C.deep} on the pale band and {@link C.mint} on the photographic one, because
   * deep green on a deep-green photo wash is unreadable.</p>
   */
  title: ReactNode;
  intro: string;
  /**
   * Turns the hero into a photograph under a green wash, per the design. Everything in the band
   * flips to light ink, and the content below stops overlapping the band — a card lifting into a
   * flat gradient reads as designed, the same card cutting a photograph in half does not.
   */
  heroImage?: { src: string };
  /** Adds the design's "Back to DAconnect" control to the header, left of the auth actions. */
  showBack?: boolean;
  /**
   * Replaces this shell's own header — the Contact page passes the public site's `SiteHeader`, so
   * it carries the same nav as Home and Find a business. A slot rather than an import here, because
   * SiteChrome imports this module's palette and importing it back would be a cycle.
   */
  header?: ReactNode;
  children: ReactNode;
}) {
  const onPhoto = Boolean(heroImage);
  useEffect(() => {
    const nodes: HTMLElement[] = FONTS.map((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    const styleEl = document.createElement("style");
    styleEl.textContent = SHELL_CSS;
    document.head.appendChild(styleEl);
    nodes.push(styleEl);
    return () => nodes.forEach((n) => n.remove());
  }, []);

  return (
    <div
      className="dcx-root"
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Figtree',system-ui,sans-serif",
        color: C.ink,
        background: C.ground,
      }}
    >
      <PageMeta title={`${metaTitle} | DAconnect`} description={metaDescription} />

      {/* HEADER */}
      {header ?? (
      <div style={{ background: "#fff", borderBottom: `1px solid ${C.line}` }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 28px",
            boxSizing: "border-box",
          }}
        >
          <Link to="/" style={{ display: "block" }}>
            <img
              src="/images/partner-network/logo.png"
              alt="DAconnect"
              style={{ height: 38, width: "auto", display: "block" }}
            />
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            {/* BackButton rather than a plain link to "/": it only promises "Back" when there is
                somewhere to go back to, and falls back to the marketplace when this page is the
                first entry in the tab. */}
            {showBack && (
              <BackButton fallbackTo="/" fallbackLabel="Back to DAconnect" color={C.deep} />
            )}
            <PublicAuthActions
              linkStyle={{ fontSize: 14, fontWeight: 600, color: "#3d423c", textDecoration: "none" }}
              pillStyle={{ ...pill, background: C.deep, color: "#fff", padding: "10px 22px" }}
              pillClassName="dcx-pill"
            />
          </div>
        </div>
      </div>
      )}

      {/* HERO BAND */}
      <div
        data-testid="shell-hero"
        style={{
          borderBottom: `1px solid ${C.bandLine}`,
          // Positioned ONLY when there is an image to position inside it. A positioned element
          // paints above the static content that follows it, so making this `relative`
          // unconditionally put the band on top of the content column — and the column lifts 28px
          // into the band, so the top of the first card disappeared behind it.
          ...(onPhoto
            ? { position: "relative", overflow: "hidden" }
            : { background: "linear-gradient(175deg,#f2faf4,#e2f3e7)" }),
        }}
      >
        {heroImage && (
          <>
            {/* alt="" on purpose. It is a backdrop under a 92%-opaque wash, and the headline on top
                of it already says everything the band means; inventing a description of scenery
                only makes a screen reader read out furniture. */}
            <img
              src={heroImage.src}
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg,rgba(20,83,45,.92) 0%,rgba(20,83,45,.82) 45%,rgba(20,83,45,.55) 100%)",
              }}
            />
          </>
        )}
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "56px 28px 48px",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".1em",
              color: onPhoto ? C.mint : C.link,
              marginBottom: 12,
            }}
          >
            {eyebrow}
          </div>
          <h1
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(30px,4.5vw,44px)",
              fontWeight: 500,
              lineHeight: 1.15,
              maxWidth: 700,
              margin: 0,
              color: onPhoto ? "#fff" : C.ink,
            }}
          >
            {title}
          </h1>
          <p
            style={{
              fontSize: 16.5,
              color: onPhoto ? C.mintInk : C.inkMuted,
              lineHeight: 1.6,
              maxWidth: 600,
              marginTop: 14,
              marginBottom: 0,
            }}
          >
            {intro}
          </p>
        </div>
      </div>

      {/* The content lifts into the band above it — the design's own -28px overlap — except under
          a photographic hero, where the designs clear it by 32px instead. */}
      <div
        data-testid="shell-content"
        style={{
          maxWidth: 1120,
          margin: onPhoto ? "32px auto 0" : "-28px auto 0",
          // Positioned so the overlap is painted the way it is drawn: the card sits ON the band,
          // whatever the band above happens to be doing.
          position: "relative",
          padding: "0 28px 56px",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {children}
      </div>

      {/* FOOTER */}
      <div data-testid="shell-footer" style={{ background: C.ink, color: C.footInk, marginTop: "auto" }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            padding: "18px 28px",
            boxSizing: "border-box",
            fontSize: 12.5,
          }}
        >
          <span>© 2026 DAconnect Pty Ltd · ABN 74 168 911 402</span>
          <span style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <Link to="/privacy" style={{ color: "inherit" }}>Privacy</Link>
            <Link to="/terms" style={{ color: "inherit" }}>Terms</Link>
            <Link to="/contact" style={{ color: "inherit" }}>Contact</Link>
            <Link to="/" style={{ color: C.mint }}>Back to DAconnect</Link>
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The gradient band that closes these pages — a heading, a line of detail, and one action.
 */
export function ClosingBand({
  heading,
  detail,
  actionLabel,
  actionTo,
}: {
  heading: string;
  detail: ReactNode;
  actionLabel: string;
  actionTo: string;
}) {
  return (
    <section
      data-testid="closing-band"
      style={{
        background: "linear-gradient(120deg,#14532d,#1c6b3c)",
        borderRadius: 20,
        marginTop: 24,
        padding: "30px 34px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 20,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 500, color: "#fff" }}>{heading}</div>
        <div style={{ fontSize: 13.5, color: C.mintInk, marginTop: 4 }}>{detail}</div>
      </div>
      <Link
        to={actionTo}
        className="dcx-pill"
        style={{ ...pill, background: "#fff", color: C.deep, flex: "none" }}
      >
        {actionLabel}
      </Link>
    </section>
  );
}
